import { useCallback, useEffect, useMemo, useState } from "react";
import IServicioIndividual from "../interfaces/IServicioIndividual";

const TTL_MS = 30 * 60 * 1000;

interface CartStored {
  items: IServicioIndividual[];
  expiresAt: number;
}

const storageKey = (pcoPasajero: string) => `mxd_cart_${pcoPasajero || "anon"}`;

const loadFromStorage = (pcoPasajero: string): IServicioIndividual[] => {
  if (!pcoPasajero) return [];
  try {
    const raw = localStorage.getItem(storageKey(pcoPasajero));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as CartStored;
    if (Date.now() > parsed.expiresAt) {
      localStorage.removeItem(storageKey(pcoPasajero));
      return [];
    }
    return parsed.items ?? [];
  } catch {
    return [];
  }
};

const saveToStorage = (pcoPasajero: string, items: IServicioIndividual[]) => {
  if (!pcoPasajero) return;
  if (!items.length) {
    localStorage.removeItem(storageKey(pcoPasajero));
    return;
  }
  const payload: CartStored = { items, expiresAt: Date.now() + TTL_MS };
  try {
    localStorage.setItem(storageKey(pcoPasajero), JSON.stringify(payload));
  } catch {
    // sin espacio o navegador estricto: ignorar
  }
};

const postToNative = (payload: object) => {
  const w = window as any;
  if (w?.ReactNativeWebView?.postMessage) {
    w.ReactNativeWebView.postMessage(JSON.stringify(payload));
  }
};

export default function useCart(pcoPasajero: string) {
  const [items, setItems] = useState<IServicioIndividual[]>(() => loadFromStorage(pcoPasajero));

  useEffect(() => {
    setItems(loadFromStorage(pcoPasajero));
  }, [pcoPasajero]);

  useEffect(() => {
    saveToStorage(pcoPasajero, items);
    postToNative({ type: "cart-badge", count: items.length });
  }, [items, pcoPasajero]);

  const has = useCallback(
    (talon: string) => items.some((it) => it.NU_TALON === talon),
    [items]
  );

  const add = useCallback((servicio: IServicioIndividual) => {
    setItems((prev) =>
      prev.some((it) => it.NU_TALON === servicio.NU_TALON) ? prev : [...prev, servicio]
    );
  }, []);

  const remove = useCallback((talon: string) => {
    setItems((prev) => prev.filter((it) => it.NU_TALON !== talon));
  }, []);

  const toggle = useCallback(
    (servicio: IServicioIndividual, state?: boolean) => {
      setItems((prev) => {
        const exists = prev.some((it) => it.NU_TALON === servicio.NU_TALON);
        if (state === true || (state === undefined && !exists)) {
          return exists ? prev : [...prev, servicio];
        }
        return prev.filter((it) => it.NU_TALON !== servicio.NU_TALON);
      });
    },
    []
  );

  const clear = useCallback(() => {
    setItems([]);
    if (pcoPasajero) {
      localStorage.removeItem(storageKey(pcoPasajero));
      postToNative({ type: "cart-empty" });
    }
  }, [pcoPasajero]);

  const total = useMemo(() => items.reduce((acc, it) => acc + it.IMP_PAGAR, 0), [items]);
  const count = items.length;

  return { items, add, remove, toggle, has, clear, total, count };
}
